<template>
  <div class="Admin__View">
    <div class="Admin__View__Profiles__Header">
      <h1>ניהול קטגוריות שאלות</h1>
      <div class="header-actions">
        <b-button 
          type="is-primary" 
          icon-left="plus"
          @click="startCreateRootCategory"
          :disabled="isCreatingRoot"
        >
          צור קטגוריה ראשית
        </b-button>
      </div>
    </div>

    <div class="Admin__View__Profiles">
      <!-- Loading State -->
      <template v-if="isLoading">
        <div class="loading-container">
          <b-loading :is-full-page="false" v-model="isLoading" :can-cancel="false" />
        </div>
      </template>

      <!-- Create Root Category Input -->
      <div v-if="isCreatingRoot" class="create-root-section">
        <div class="create-root-content">
          <input
            ref="rootInput"
            v-model="newRootName"
            type="text"
            class="root-input"
            placeholder="שם קטגוריה ראשית חדשה..."
            @keyup.enter="saveRootCategory"
            @keyup.esc="cancelCreateRoot"
          />
          <input
            ref="descriptionInput"
            v-model="newRootDescription"
            type="text"
            class="root-description-input"
            placeholder="תיאור קטגוריה ראשית חדשה..."
            @keyup.enter="saveRootCategory"
            @keyup.esc="cancelCreateRoot"
          />
          <div class="root-actions">
            <b-button 
              type="is-success" 
              icon-left="check"
              @click="saveRootCategory"
            >
              שמור
            </b-button>
            <b-button 
              type="is-danger" 
              icon-left="close"
              @click="cancelCreateRoot"
            >
              ביטול
            </b-button>
          </div>
        </div>
      </div>

      <!-- Categories Tree -->
      <template v-if="!isLoading">
        <div v-if="categories.length === 0" class="no-categories">
          <b-icon icon="folder-open" size="is-large" />
          <p>אין עדיין קטגוריות. לחץ על "צור קטגוריה ראשית" כדי להתחיל.</p>
        </div>
        
        <div v-else class="Admin__View__Profiles__Tree">
          <TreeItemCRUD
            v-for="category in categories"
            :key="category.id"
            :item="category"
            @edit="handleEdit"
            @add-child="handleAddChild"
            @move="handleMove"
            @delete="handleDelete"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import AdminService from '@/services/AdminService';
import TreeItemCRUD from '@/components/TreeItemCRUD.vue';
import DialogMoveCategory from '@/components/DialogMoveCategory.vue';
import { 
  ModalProgrammatic as Modal,
  DialogProgrammatic as Dialog,
  ToastProgrammatic as Toast 
} from 'buefy';

export default {
  name: 'AdminManageQuestionsCategories',
  components: {
    TreeItemCRUD
  },
  data() {
    return {
      categories: [],
      isLoading: false,
      isCreatingRoot: false,
      newRootName: '',
      newRootDescription: '',
    };
  },
  created() {
    this.loadCategories();
  },
  methods: {
    // Load Categories
    loadCategories() {
      this.isLoading = true;
      AdminService.getQuestionCategoriesTree()
        .then((r) => {
          this.categories = r.data || [];
        })
        .catch((error) => {
          console.error('Error loading categories:', error);
          Toast.open({
            message: 'שגיאה בטעינת קטגוריות',
            type: 'is-danger',
            duration: 5000
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    // Create Root Category
    startCreateRootCategory() {
      this.isCreatingRoot = true;
      this.newRootName = '';
      this.newRootDescription = '';
      this.$nextTick(() => {
        if (this.$refs.rootInput) {
          this.$refs.rootInput.focus();
        }
      });
    },

    saveRootCategory() {
      if (!this.newRootName.trim()) {
        Toast.open({
          message: 'שם הקטגוריה לא יכול להיות ריק',
          type: 'is-danger',
          duration: 3000
        });
        return;
      }

      this.createCategory(null, this.newRootName.trim(), this.newRootDescription.trim());
    },

    cancelCreateRoot() {
      this.isCreatingRoot = false;
      this.newRootName = '';
      this.newRootDescription = '';
    },

    // Create Category (root or child)
    createCategory(parentId, name, description) {
      const data = {
        name,
        description,
        parentId,
      };

      AdminService.createQuestionCategory(data)
        .then((r) => {
          Toast.open({
            message: 'קטגוריה נוצרה בהצלחה',
            type: 'is-success',
            duration: 3000
          });
          
          // Reload tree to get updated structure
          this.loadCategories();
          
          if (parentId === null) {
            this.cancelCreateRoot();
          }
        })
        .catch((error) => {
          console.error('Error creating category:', error);
          Toast.open({
            message: `שגיאה ביצירת קטגוריה: ${this.getServerError(error)}`,
            type: 'is-danger',
            duration: 5000
          });
        });
    },

    getServerError(error) {
      const data = error && error.response && error.response.data;
      if (!data) return (error && error.message) || 'שגיאה לא ידועה';
      if (typeof data === 'string') return data;
      return data.message || data.Message || data.error || data.title || JSON.stringify(data);
    },

    // Handle Edit from TreeItemCRUD
    handleEdit(payload) {
      const { id, name, parentId, description } = payload;
      this.updateCategory(id, name, parentId, description);
    },

    // Update Category
    updateCategory(categoryId, name, parentId, description) {
      const data = {
        name,
        description,
        parentId
      };

      AdminService.updateQuestionCategory(categoryId, data)
        .then((r) => {
          Toast.open({
            message: 'קטגוריה עודכנה בהצלחה',
            type: 'is-success',
            duration: 3000
          });
          
          // Update the category in place without full reload
          this.updateCategoryInTree(categoryId, name, description);
        })
        .catch((error) => {
          console.error('Error updating category:', error);
          Toast.open({
            message: `שגיאה בעדכון קטגוריה: ${this.getServerError(error)}`,
            type: 'is-danger',
            duration: 5000
          });
        });
    },

    // Handle Add Child from TreeItemCRUD
    handleAddChild(payload) {
      const { parentId, name, description } = payload;
      this.createCategory(parentId, name, description);
    },

    // Handle Move from TreeItemCRUD
    handleMove(payload) {
      const { id, name, currentParentId, item } = payload;
      
      Modal.open({
        component: DialogMoveCategory,
        props: {
          categoryId: id,
          categoryName: name,
          currentParentId: currentParentId,
          allCategories: this.categories
        },
        events: {
          confirm: (moveData) => {
            this.moveCategory(moveData.categoryId, moveData.newParentId);
            Modal.close();
          }
        },
        canCancel: ['escape', 'outside'],
        trapFocus: true
      });
    },

    // Move Category
    moveCategory(categoryId, newParentId) {
      const category = this.findCategoryById(categoryId, this.categories);
      if (!category) return;

      const data = {
        name: category.name,
        parentId: newParentId
      };

      AdminService.updateQuestionCategory(categoryId, data)
        .then((r) => {
          Toast.open({
            message: 'קטגוריה הועברה בהצלחה',
            type: 'is-success',
            duration: 3000
          });
          
          // Reload to reflect the new structure
          this.loadCategories();
        })
        .catch((error) => {
          console.error('Error moving category:', error);
          Toast.open({
            message: `שגיאה בהעברת קטגוריה: ${this.getServerError(error)}`,
            type: 'is-danger',
            duration: 5000
          });
        });
    },

    // Handle Delete from TreeItemCRUD
    handleDelete(payload) {
      const { id, name, childCount } = payload;
      
      let message = `האם אתה בטוח שברצונך למחוק את הקטגוריה "${name}"?`;
      if (childCount > 0) {
        message += `\n\nשים לב: פעולה זו תמחק גם ${childCount} קטגוריות בנות.`;
      }

      Dialog.confirm({
        title: 'אישור מחיקה',
        message: message,
        confirmText: 'מחק',
        cancelText: 'ביטול',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          this.deleteCategory(id);
        }
      });
    },

    // Delete Category (cascade)
    deleteCategory(categoryId) {
      AdminService.deleteQuestionCategory(categoryId)
        .then((r) => {
          Toast.open({
            message: 'קטגוריה נמחקה בהצלחה',
            type: 'is-success',
            duration: 3000
          });
          
          // Remove from tree
          this.removeCategoryFromTree(categoryId);
        })
        .catch((error) => {
          console.log('Error deleting category:', error.response);
          if (error.response?.status === 400) {
            Toast.open({
              message: error.response?.data,
              type: 'is-danger',
              duration: 5000
            });
          } else {
            Toast.open({
              message: 'שגיאה במחיקת קטגוריה',
              type: 'is-danger',
              duration: 5000
            });
          }
        });
    },

    // Helper: Find category by ID
    findCategoryById(id, categories) {
      for (const cat of categories) {
        if (cat.id === id) return cat;
        if (cat.children) {
          const found = this.findCategoryById(id, cat.children);
          if (found) return found;
        }
      }
      return null;
    },

    // Helper: Update category name in tree (local update)
    updateCategoryInTree(categoryId, newName) {
      const updateRecursive = (categories) => {
        for (const cat of categories) {
          if (cat.id === categoryId) {
            cat.name = newName;
            return true;
          }
          if (cat.children && updateRecursive(cat.children)) {
            return true;
          }
        }
        return false;
      };
      
      updateRecursive(this.categories);
    },

    // Helper: Remove category from tree (local update)
    removeCategoryFromTree(categoryId) {
      const removeRecursive = (categories, parent = null) => {
        for (let i = 0; i < categories.length; i++) {
          if (categories[i].id === categoryId) {
            categories.splice(i, 1);
            return true;
          }
          if (categories[i].children && removeRecursive(categories[i].children, categories[i])) {
            return true;
          }
        }
        return false;
      };
      
      removeRecursive(this.categories);
    },

    // Helper: Flatten tree to array
    flattenTree(categories) {
      const result = [];
      const flatten = (cats) => {
        cats.forEach(cat => {
          result.push(cat);
          if (cat.children) {
            flatten(cat.children);
          }
        });
      };
      flatten(categories);
      return result;
    }
  }
};
</script>

<style lang="scss" scoped>
.Admin__View {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.Admin__View__Profiles__Header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  background-color: #f5f5f5;
  border-bottom: 2px solid #e0e0e0;

  h1 {
    margin: 0;
    font-size: 24px;
    color: #2c3e50;
  }
}

.header-actions {
  display: flex;
  gap: 10px;
}

.Admin__View__Profiles {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  background-color: #fafafa;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

.create-root-section {
  margin-bottom: 20px;
  padding: 20px;
  background-color: #e7f3ff;
  border: 2px solid #3273dc;
  border-radius: 6px;
}

.create-root-content {
  display: flex;
  gap: 15px;
  align-items: center;
}

.root-input {
  flex: 1;
  padding: 12px 15px;
  border: 2px solid #3273dc;
  border-radius: 4px;
  font-size: 15px;
  outline: none;

  &:focus {
    border-color: #1e5a9e;
  }
}

.root-description-input {
  width: 100%;
  padding: 12px 15px;
  border: 2px solid #3273dc;
  border-radius: 4px;
  font-size: 15px;
  outline: none;
}

.root-sort-order-input {
  width: 100px;
  padding: 12px 15px;
  border: 2px solid #3273dc;
  border-radius: 4px;
  font-size: 15px;
  outline: none;
}
.root-actions {
  display: flex;
  gap: 10px;
}

.no-categories {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #999;

  .icon {
    margin-bottom: 20px;
    opacity: 0.5;
  }

  p {
    font-size: 16px;
    text-align: center;
  }
}

.Admin__View__Profiles__Tree {
  border-radius: 6px;
  overflow-y: scroll;
  position: relative;
}
</style>
